import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { useSeoMeta } from '../hooks/useSeoMeta';
import { OG_IMAGE_URL } from '../pages/home-redesign/cityLinks';
import LegalPageShell from '../components/LegalPageShell';

type SafetySubsection = {
  heading?: string;
  paragraphs?: string[];
  items?: string[];
};

type SafetySection = SafetySubsection & {
  heading: string;
  subsections?: SafetySubsection[];
};

const Safety = () => {
  const { t, currentLanguage } = useLanguage();
  const sections = t('safety.sections') as unknown as SafetySection[];

  useSeoMeta({
    title: t('safety.seoTitle'),
    description: t('safety.seoDescription'),
    url: 'https://chisme.social/safety',
    image: OG_IMAGE_URL,
    locale: currentLanguage === 'en' ? 'en' : 'es',
  });

  const renderBlock = (block: SafetySubsection, keyPrefix: string) => (
    <>
      {block.paragraphs?.map((paragraph, index) => (
        <p key={`${keyPrefix}-p-${index}`}>{paragraph}</p>
      ))}
      {block.items && (
        <ul>
          {block.items.map((item, index) => (
            <li key={`${keyPrefix}-li-${index}`}>{item}</li>
          ))}
        </ul>
      )}
    </>
  );

  return (
    <LegalPageShell
      backLabel={t('safety.backHome')}
      title={t('safety.title')}
      lastUpdatedLabel={t('safety.lastUpdated')}
      lastUpdatedDate={t('safety.lastUpdatedDate')}
    >
      <p>{t('safety.intro')}</p>

      {sections.map((section, sectionIndex) => (
        <section key={section.heading}>
          <h2>{section.heading}</h2>
          {renderBlock(section, `section-${sectionIndex}`)}
          {section.subsections?.map((subsection, subsectionIndex) => (
            <React.Fragment key={subsection.heading ?? `${section.heading}-${subsectionIndex}`}>
              {subsection.heading && <h3>{subsection.heading}</h3>}
              {renderBlock(subsection, `section-${sectionIndex}-subsection-${subsectionIndex}`)}
            </React.Fragment>
          ))}
        </section>
      ))}
    </LegalPageShell>
  );
};

export default Safety;
